/**
 * Microsoft Clarity custom event + tag tracking.
 *
 * The Clarity loader stub is injected in layout/theme.liquid and queues any
 * `clarity(...)` calls made before the remote script finishes downloading, so
 * every call in this file is safe to make immediately.
 *
 * Four Clarity primitives are used:
 *   - clarity("event", name)      -> a discrete action, filterable alongside Smart Events
 *   - clarity("set", key, value)  -> a session tag, used to segment recordings
 *   - clarity("upgrade", reason)  -> force this session to be kept when Clarity samples
 *   - clarity("identify", id)     -> link a logged-in shopper's sessions together
 *
 * Tags are page-scoped in Clarity, so anything that must survive navigation
 * (search-used, conversion funnel state) is mirrored into sessionStorage and
 * re-applied on every page load. See replaySessionTags().
 */

const SESSION_TAG_KEY = 'fbc_clarity_session_tags';
const TERM_MAX_LENGTH = 60;
const TYPING_DEBOUNCE_MS = 700;
const MOBILE_BREAKPOINT = '(max-width: 900px)';
const LABEL_MAX_LENGTH = 40;
const CHECKOUT_HANDOFF_KEY = 'fbc_clarity_checkout_handoff';
const CHECKOUT_HANDOFF_TTL_MS = 60 * 60 * 1000;
const ARTICLE_DEPTH_MARKS = [50, 100];
const MAX_JS_ERRORS_PER_PAGE = 2;
const NEWSLETTER_OUTCOME_WATCH_MS = 15000;
const QA_PARAM = 'clarity_qa';
const DEBUG_PARAM = 'clarity_debug';
const DEBUG_KEY = 'fbc_clarity_debug';
const PREVIEW_THEME_PARAM = 'preview_theme_id';

const SELECTORS = {
  searchToggle: '[header-search-toggle]',
  searchInput: '[header-search-form-input], [search-section-form-input]',
  searchForm: '.header__search-form, .search-section__search-form',
  searchClose: '[header-search-close-btn], .search-close-btn',
  predictiveWrapper: '.predictive-search',
  predictiveResult: '.predictive-search__results-item a',
  predictiveNoResults: '.predictive-search__no-results',
  predictiveViewAll: '.predictive-search__view-all-btn',
  cartDrawer: 'cart-drawer',
  cartDrawerScope: '#shopify-section-cartx-drawer',
  cartItem: '.cartx-drawer-product-item',
  addToCart: '.cartx-add-to-cart',
  addToCartDisabled: '.cartx-disabled, .cartx-sold-out, .cartx-max-stock',
  upsellItem: '.cartx-upsell-product-item',
  bogoAdd: '.cartx-bogo-offer-add-to-cart',
  giftClaim: '.cartx-claim-add-to-cart',
  giftUnclaim: '.cartx-claim-remove-from-cart',
  quantityButton: '.cartx-drawer-product-quantity-button',
  productButtonWrap: '.cartx-product-button-wrap',
  pdpTitle: '.product-single__details-title',
  discountApply: '#cartx-apply-code-button, .cartx-apply-code__button, .prv-submit-button',
  discountInput: '#cartx-discount-code, .cartx-apply-code-input-text, .prv-coupon-input',
  discountMessage: '.cartx-apply-code__message',
  discountRemove: '#cartx-remove-btn',
  checkoutButton: '.cartx-checkout-button',
  emptyCartUpsell: '.cartx-empty-upsell-btn',
  headerCartCount: '[header-cart-item-count]',
  customerIdNode: '.wc_customer_id',

  pdpVariantDropdown: '.custom-dropdown__selected',
  pdpVariantOption: '.custom-dropdown__option',
  pdpGalleryArrow: '.product-single__media-arrow',
  pdpAccordionButton: '[product-single-accordion-btn], .product-single__accordion-btn',
  pdpAccordion: '.product-single__accordion',
  pdpAccordionTitle: '.accordion-title-text, .product-single__accordion-btn',
  pdpDescriptionExpand: '.product-single__details-description-excerpt-btn',
  pdpQuantityIncrement: '[product-single-form-quantity-increment]',
  pdpQuantityDecrement: '[product-single-form-quantity-decrement]',
  pdpReviews: '.product-single__details-reviews',
  pdpWriteReview: '.product-single__details-write-review',
  pdpPaymentInfo: '.product-single__afterpays',
  pdpSoldOut: '.cartx-sold-out',

  quickViewTrigger: '[quick-view-btn]',
  quickViewOverlay: '#custom-overlay',
  quickViewClose: '.quickview-close',

  plpSortingsToggle: '[collection-sortings-toggle]',
  plpFilterOption: '[collection-sortings-filter-option]',
  plpSortOption: '[collection-sortings-sort-option]',
  plpEmpty: '.collection-listing__items-empty',
  plpItemsWrapper: '[collection-items-wrapper]',
  plpItemsCount: '[collection-items-count]',
  plpSubcollection: '.collection-listing__feat-collections-item-link',
  plpPromoBanner: '.collection-listing__item-promotion-banner-link',
  pagination: '.pagination__item.link',
  productCard: '.product-card',
  productCardLink: '.product-card__media-link, .product-card__title',
  recommendationsScope: '.m-store-carousel',
  featuredProductsScope: '.m-feat-products',
  collectionGridScope: '.collection-listing__items',

  navMobileTrigger: '[header-mob-menu-trigger]',
  navMobileClose: '[header-mob-menu-close]',
  // Desktop mega-menu and mobile drawer are separate DOM trees with separate
  // class names — both have to be listed or one device tracks nothing.
  navMenuLink: '.header__menu-item-link, .header__mob-menu-item-link',
  navSubLink: [
    '.header__menu-list-sub-col-link',
    '.header__menu-list-sub-col-sub-link',
    '.header__menu-list-sub-col-featured-link',
    '.header__mob-menu-item-sub-link',
    '.header__mob-menu-item-sub-btn',
    '.header__mob-menu-item-sub-featured-link',
  ].join(', '),
  navAccount: '.header__actions-action--account-btn',
  navLogo: '.header__logo-link',
  announcementLink: '.header__announcement-bar-item a',
  footerLink: '.footer__column-list-link',

  wishlistToggle: '.th_wl_col_btn',
  wishlistView: 'a[href*="/apps/wishlist"]',

  newsletterSuccess: '#ContactFooter-success, .newsletter-form__message--success',
  newsletterError: '#ContactFooter-error',
  accountErrorSummary: '.login-register__form-error-summary',
  accountFieldError: '.input-error',

  heroCta: '.page-header__button',
  socialLink: 'a[href*="instagram.com"], a[href*="tiktok.com"], a[href*="facebook.com"], a[href*="youtube.com"]',
  articleContent: '.article__content',
};

/* -------------------------------------------------------------------------- */
/* Clarity primitives                                                          */
/* -------------------------------------------------------------------------- */

/**
 * ?clarity_debug=1 mirrors every Clarity call to the console — including the
 * page-load ones that fire before a devtools snippet could be pasted, which is
 * the only way to verify things like newsletter_signup_succeeded or
 * page_not_found by hand. Persisted for the session so it survives navigation;
 * ?clarity_debug=0 turns it back off.
 */
const isDebug = (() => {
  try {
    const flag = new URLSearchParams(window.location.search).get(DEBUG_PARAM);
    if (flag === '1') sessionStorage.setItem(DEBUG_KEY, '1');
    if (flag === '0') sessionStorage.removeItem(DEBUG_KEY);
    return sessionStorage.getItem(DEBUG_KEY) === '1';
  } catch (error) {
    return false;
  }
})();

const callClarity = (...args) => {
  if (isDebug) console.log('%c[clarity]', 'color:#7c3aed;font-weight:700', ...args);
  if (typeof window.clarity !== 'function') return;
  try {
    window.clarity(...args);
  } catch (error) {
    /* Never let analytics break the storefront. */
  }
};

const sendEvent = (name) => callClarity('event', name);

const setTag = (key, value) => {
  if (!key || value === null || value === undefined || value === '') return;
  callClarity('set', key, String(value));
};

const upgradeSession = (reason) => callClarity('upgrade', reason);

/* -------------------------------------------------------------------------- */
/* Session-scoped tags                                                         */
/* -------------------------------------------------------------------------- */

const readSessionTags = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_TAG_KEY) ?? '{}');
  } catch (error) {
    return {};
  }
};

const writeSessionTags = (tags) => {
  try {
    sessionStorage.setItem(SESSION_TAG_KEY, JSON.stringify(tags));
  } catch (error) {
    /* Private browsing / storage full — tags stay page-scoped only. */
  }
};

/** Set a tag AND persist it so it is re-applied on every later page of the session. */
const setSessionTag = (key, value) => {
  if (!key || value === null || value === undefined || value === '') return;
  setTag(key, value);
  const tags = readSessionTags();
  tags[key] = String(value);
  writeSessionTags(tags);
};

const getSessionTag = (key) => readSessionTags()[key] ?? null;

const replaySessionTags = () => {
  const tags = readSessionTags();
  for (const [key, value] of Object.entries(tags)) setTag(key, value);
};

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

const isMobile = () => window.matchMedia(MOBILE_BREAKPOINT).matches;

const deviceLabel = () => (isMobile() ? 'mobile' : 'desktop');

/**
 * Search terms are sent to Clarity explicitly (not scraped from the recording),
 * so they bypass Clarity's input masking. Anything that looks like an email or
 * a long free-text blob is dropped rather than tagged.
 */
const sanitiseTerm = (rawTerm) => {
  const term = (rawTerm ?? '').trim();
  if (!term || term.length > TERM_MAX_LENGTH) return '';
  if (term.includes('@')) return '';
  return term.toLowerCase();
};

const usedSearch = () => getSessionTag('used_search') === 'yes';

/**
 * The query currently in whichever search field holds one — the header form and
 * the /search form can both be on the page at once.
 */
const liveSearchTerm = () => {
  const inputs = [...document.querySelectorAll(SELECTORS.searchInput)];
  const filled = inputs.find((input) => input.value?.trim());
  return sanitiseTerm(filled?.value);
};

const cartSnapshot = () => {
  const cart = window.user_cart;
  if (!cart) return null;
  return {
    itemCount: cart.item_count ?? cart.items?.length ?? 0,
    // Bucketed, not formatted as currency — Liquid owns money formatting.
    totalCents: cart.total_price ?? 0,
  };
};

const tagCartState = () => {
  const snapshot = cartSnapshot();
  if (!snapshot) return;
  setTag('cart_item_count', snapshot.itemCount);
};

const productNameFrom = (element) => {
  const title = element?.querySelector(
    '.cartx-drawer-product-title, .cartx-upsell-item-title, .cartx-claim-item-title, .product-card__title'
  );
  return title?.textContent?.trim() ?? '';
};

/** Trimmed, length-capped visible text — used for menu/accordion/CTA labels. */
const labelFrom = (element) => {
  const text = element?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  return text.slice(0, LABEL_MAX_LENGTH);
};

/**
 * Some third-party scripts on this store dispatch synthetic events whose target
 * is `document` rather than an element, and `document` has no closest(). Every
 * delegated handler resolves its target through this so a foreign event cannot
 * throw inside analytics code.
 */
const elementTarget = (event) => {
  const target = event?.target;
  return typeof target?.closest === 'function' ? target : null;
};

const templateName = () => document.body?.dataset.template ?? '';

const isTemplate = (name) => templateName() === name;

/** Where in the site a product card was clicked from. */
const productCardSource = (card) => {
  if (card.closest(SELECTORS.predictiveWrapper)) return 'predictive_search';
  if (card.closest(SELECTORS.quickViewOverlay)) return 'quick_view';
  if (card.closest(SELECTORS.recommendationsScope)) return 'recommendations';
  if (card.closest(SELECTORS.featuredProductsScope)) return 'featured_products';
  if (card.closest(SELECTORS.collectionGridScope)) return 'collection_grid';
  return 'other';
};

/**
 * Server-rendered cart count from the header. Read instead of window.user_cart
 * because cartx.js populates that asynchronously, and the conversion check runs
 * on the first paint.
 */
const renderedCartCount = () => {
  const node = document.querySelector(SELECTORS.headerCartCount);
  if (!node) return null;
  const count = Number(node.getAttribute('header-cart-item-count') ?? node.textContent);
  return Number.isFinite(count) ? count : null;
};

/* -------------------------------------------------------------------------- */
/* Search module                                                               */
/* -------------------------------------------------------------------------- */

const searchState = {
  open: false,
  // A search "episode" spans one shopper intent. It outlives `open`, which only
  // reflects whether the UI is believed to be showing.
  episode: false,
  term: '',
  queryTypedSent: false, // Dedupes search_query_typed within an episode.
  hasQuery: false, // A query was entered at all — this is what abandonment turns on.
  resolved: false, // A result was clicked / search submitted — i.e. not abandoned.
  resultsSeen: false,
  noResultsTerm: '', // Dedupes search_no_results while the shopper keeps typing.
};

let typingTimer = null;

const openSearchSession = (origin) => {
  if (searchState.open) return;
  searchState.open = true;

  // The mobile search bar bounces focus while header-form-script.js opens the
  // overlay, and its close button sits under the tap target — both close the
  // session without producing an outcome. Refocusing after that is the same
  // search continuing, not a new one, so it must not re-fire search_opened.
  if (searchState.episode) return;

  searchState.episode = true;
  searchState.term = '';
  searchState.queryTypedSent = false;
  searchState.hasQuery = false;
  searchState.resolved = false;
  searchState.resultsSeen = false;
  searchState.noResultsTerm = '';

  sendEvent('search_opened');
  setTag('search_device', deviceLabel());
  setTag('search_open_origin', origin);
  setSessionTag('used_search', 'yes');

  // Guarantees this session survives Clarity's sampling, so the cursor
  // movement over the search module is always reviewable in playback.
  upgradeSession('search_interaction');
};

const resolveSearchSession = (outcome, eventName) => {
  searchState.resolved = true;
  searchState.hasQuery = true;
  searchState.episode = false;

  // The debounce may not have fired yet on a fast type-then-click, so fall
  // back to reading the live field rather than losing the term entirely.
  if (!searchState.term) searchState.term = liveSearchTerm();

  sendEvent(eventName);
  setSessionTag('search_outcome', outcome);
  if (searchState.term) setSessionTag('search_term', searchState.term);
};

const closeSearchSession = () => {
  if (!searchState.open) return;

  // Only a typed-but-unresolved close is a real abandonment, and only a real
  // outcome ends the episode.
  if (searchState.hasQuery && !searchState.resolved) {
    sendEvent('search_abandoned');
    setSessionTag('search_outcome', 'abandoned');
    if (searchState.term) setSessionTag('search_term', searchState.term);
    searchState.episode = false;
  }

  searchState.open = false;
};

const handleSearchTyping = (input) => {
  const term = sanitiseTerm(input.value);
  if (!term || term.length < 2) return;

  searchState.term = term;

  searchState.hasQuery = true;

  if (!searchState.queryTypedSent) {
    searchState.queryTypedSent = true;
    sendEvent('search_query_typed');
  }
};

const observePredictiveResults = (wrapper) => {
  const observer = new MutationObserver(() => {
    if (!searchState.open) return;

    // Predictive results routinely render faster than the 700 ms typing
    // debounce, so read the field directly instead of waiting for
    // searchState.term to be populated — gating on it made both events race.
    const term = searchState.term || liveSearchTerm();
    if (!term) return;

    searchState.hasQuery = true;

    const hasResults = wrapper.querySelector('.predictive-search__results-item');
    const noResults = wrapper.querySelector(SELECTORS.predictiveNoResults);

    if (hasResults && !searchState.resultsSeen) {
      searchState.resultsSeen = true;
      sendEvent('search_results_shown');
      return;
    }

    if (noResults && searchState.noResultsTerm !== term) {
      searchState.noResultsTerm = term;
      sendEvent('search_no_results');
      setSessionTag('search_zero_results', 'yes');
      setSessionTag('search_term', term);
    }
  });

  observer.observe(wrapper, { childList: true, subtree: true });
};

const initSearchTracking = () => {
  // Desktop: the header toggle button reveals the form.
  document.addEventListener(
    'click',
    (event) => {
      if (elementTarget(event)?.closest(SELECTORS.searchToggle)) openSearchSession('header_toggle');
    },
    true
  );

  // Mobile + desktop: focusing the field is the real "search started" signal.
  document.addEventListener(
    'focusin',
    (event) => {
      if (elementTarget(event)?.closest(SELECTORS.searchInput)) openSearchSession('input_focus');
    },
    true
  );

  document.addEventListener('input', (event) => {
    const input = elementTarget(event)?.closest(SELECTORS.searchInput);
    if (!input) return;

    openSearchSession('input_focus');
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => handleSearchTyping(input), TYPING_DEBOUNCE_MS);
  });

  document.addEventListener(
    'click',
    (event) => {
      if (elementTarget(event)?.closest(SELECTORS.searchClose)) closeSearchSession();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = elementTarget(event);
      if (!target) return;

      const result = target.closest(SELECTORS.predictiveResult);
      if (result) {
        const name = productNameFrom(result.closest('.predictive-search__results-item'));
        if (name) setTag('search_result_clicked', name);
        resolveSearchSession('result_clicked', 'search_result_clicked');
        return;
      }

      if (target.closest(SELECTORS.predictiveViewAll)) {
        resolveSearchSession('view_all_results', 'search_view_all_clicked');
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const target = elementTarget(event);
      if (!target?.closest(SELECTORS.searchForm)) return;

      const input = target.querySelector(SELECTORS.searchInput);
      if (input) searchState.term = sanitiseTerm(input.value) || searchState.term;
      resolveSearchSession('submitted', 'search_submitted');
    },
    true
  );

  for (const wrapper of document.querySelectorAll(SELECTORS.predictiveWrapper)) {
    observePredictiveResults(wrapper);
  }

  // Leaving the page with a typed query and no interaction is an abandonment.
  window.addEventListener('pagehide', closeSearchSession);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') closeSearchSession();
  });
};

/** Fired on /search — the destination of a submitted search. */
const trackSearchResultsPage = () => {
  // Allows for an optional market/locale prefix, e.g. /en-au/search.
  if (!/^(\/[a-z]{2}(-[a-z]{2})?)?\/search(\/|$)/i.test(window.location.pathname)) return;

  const term = sanitiseTerm(new URLSearchParams(window.location.search).get('q'));
  if (!term) return;

  sendEvent('search_results_page_viewed');
  setSessionTag('used_search', 'yes');
  setSessionTag('search_term', term);
  setTag('search_device', deviceLabel());
  upgradeSession('search_interaction');

  const countNode = document.querySelector('[search-header-count] span');
  const hasResults = Boolean(document.querySelector('.collection-listing__item'));

  if (!hasResults) {
    sendEvent('search_results_page_empty');
    setSessionTag('search_zero_results', 'yes');
  } else if (countNode) {
    setTag('search_results_count', countNode.textContent.trim());
  }
};

/* -------------------------------------------------------------------------- */
/* Cart module                                                                 */
/* -------------------------------------------------------------------------- */

/** Mirrors search usage into the cart funnel so conversion can be segmented. */
const tagFunnelStep = (step) => {
  setSessionTag(step, 'yes');
  if (usedSearch()) setSessionTag(`${step}_after_search`, 'yes');
};

const trackAddToCart = (button) => {
  const wrap = button.closest(SELECTORS.productButtonWrap);
  const upsell = button.closest(SELECTORS.upsellItem);

  let source = 'product_page';
  if (upsell) source = 'cart_upsell';
  else if (button.closest(SELECTORS.cartDrawer)) source = 'cart_drawer';
  else if (button.closest(SELECTORS.quickViewOverlay)) source = 'quick_view';
  else if (button.closest('.collection-listing__item')) source = 'collection_tile';

  sendEvent('cart_item_added');
  setTag('add_to_cart_source', source);

  const name =
    productNameFrom(upsell) ||
    productNameFrom(button.closest('.collection-listing__item')) ||
    (source === 'product_page' ? document.querySelector(SELECTORS.pdpTitle)?.textContent?.trim() : '');
  if (name) setTag('add_to_cart_product', name);
  if (wrap?.dataset.variantId) setTag('add_to_cart_variant', wrap.dataset.variantId);

  tagFunnelStep('added_to_cart');
  upgradeSession('add_to_cart');
  tagCartState();
};

const trackQuantityChange = (button) => {
  const item = button.closest(SELECTORS.cartItem);
  const currentQty = Number(item?.dataset.currentQty ?? 0);
  const isDecrease = button.getAttribute('name') === 'minus';

  // At quantity 1 the minus control is rendered as a bin icon — that is a removal.
  if (isDecrease && currentQty <= 1) {
    sendEvent('cart_item_removed');
  } else {
    sendEvent(isDecrease ? 'cart_quantity_decreased' : 'cart_quantity_increased');
  }

  const name = productNameFrom(item);
  if (name) setTag('cart_quantity_changed_product', name);
  upgradeSession('cart_interaction');
};

const observeDiscountMessage = () => {
  const INTERMEDIATE = ['applying discount...', 'removing discount...', 'applying discount'];
  let pendingCode = null;

  // The drawer and the cart page can each render a code field; take whichever
  // one the shopper actually typed into.
  const capturePendingCode = () => {
    const inputs = [...document.querySelectorAll(SELECTORS.discountInput)];
    const code = inputs.find((input) => input.value?.trim())?.value.trim();
    if (code) pendingCode = code.toUpperCase();
  };

  document.addEventListener(
    'click',
    (event) => {
      if (elementTarget(event)?.closest(SELECTORS.discountApply)) capturePendingCode();
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Enter' && elementTarget(event)?.closest(SELECTORS.discountInput)) capturePendingCode();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!elementTarget(event)?.closest(SELECTORS.discountRemove)) return;
      sendEvent('cart_discount_removed');
    },
    true
  );

  // reload_cartx() replaces .cartx-drawer-inner's innerHTML on every cart update,
  // so the message node is destroyed and recreated. Observe the stable section
  // wrapper and re-query the message node on each mutation instead.
  const scope = document.querySelector(SELECTORS.cartDrawerScope);
  if (!scope) return;

  let lastMessage = '';

  const observer = new MutationObserver(() => {
    const messageNode = scope.querySelector(SELECTORS.discountMessage);
    const message = messageNode?.textContent?.trim() ?? '';

    if (!message || message === lastMessage) return;
    lastMessage = message;

    if (!pendingCode) return;
    if (INTERMEDIATE.includes(message.toLowerCase())) return;

    const failed =
      messageNode.classList.contains('error') || /invalid|not valid|cannot|expired|isn't/i.test(message);

    sendEvent(failed ? 'cart_discount_failed' : 'cart_discount_applied');
    setTag('cart_discount_code', pendingCode);
    if (failed) setTag('cart_discount_error', message.slice(0, TERM_MAX_LENGTH));

    pendingCode = null;
  });

  observer.observe(scope, { childList: true, characterData: true, subtree: true });
};

const observeCartDrawerOpen = () => {
  const drawer = document.querySelector(SELECTORS.cartDrawer);
  if (!drawer) return;

  let wasOpen = drawer.classList.contains('drawer-open');

  const observer = new MutationObserver(() => {
    const isOpen = drawer.classList.contains('drawer-open');
    if (isOpen === wasOpen) return;
    wasOpen = isOpen;

    if (isOpen) {
      sendEvent('cart_opened');
      setTag('cart_device', deviceLabel());
      tagFunnelStep('viewed_cart');
      tagCartState();
      upgradeSession('cart_interaction');
    } else {
      sendEvent('cart_closed');
    }
  });

  observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
};

const initCartTracking = () => {
  document.addEventListener(
    'click',
    (event) => {
      const target = elementTarget(event);
      if (!target) return;

      const addButton = target.closest(SELECTORS.addToCart);
      if (addButton && !addButton.closest(SELECTORS.addToCartDisabled) && !addButton.matches(SELECTORS.addToCartDisabled)) {
        trackAddToCart(addButton);
        return;
      }

      if (target.closest(SELECTORS.bogoAdd)) {
        sendEvent('cart_bogo_offer_added');
        tagFunnelStep('added_to_cart');
        return;
      }

      if (target.closest(SELECTORS.giftClaim)) {
        sendEvent('cart_gift_claimed');
        return;
      }

      if (target.closest(SELECTORS.giftUnclaim)) {
        sendEvent('cart_gift_removed');
        return;
      }

      const quantityButton = target.closest(SELECTORS.quantityButton);
      if (quantityButton) {
        trackQuantityChange(quantityButton);
        return;
      }

      if (target.closest(SELECTORS.checkoutButton)) {
        sendEvent('cart_checkout_clicked');
        tagFunnelStep('reached_checkout');
        tagCartState();
        markCheckoutHandoff();
        upgradeSession('checkout_intent');
        return;
      }

      // Note: cart_closed is not fired here — observeCartDrawerOpen() already
      // covers the close button, the overlay click and the Escape path.
      if (target.closest(SELECTORS.emptyCartUpsell)) sendEvent('cart_empty_explore_clicked');
    },
    true
  );

  observeCartDrawerOpen();
  observeDiscountMessage();
};

/* -------------------------------------------------------------------------- */
/* Identity + conversion correlation                                           */
/* -------------------------------------------------------------------------- */

/**
 * Theme previews run on the live domain, so preview traffic lands in the same
 * Clarity project as real shoppers. Tagging the session is what makes a QA run
 * findable afterwards: Recordings -> Custom tags -> qa_session = yes.
 *
 * Triggered by ?clarity_qa=1, or automatically by Shopify's own
 * ?preview_theme_id=... on the entry page of a preview session. Once either has
 * been seen the tag is persisted, so it covers the rest of the session even
 * though Shopify drops the parameter from later URLs.
 */
const initQaSession = () => {
  const params = new URLSearchParams(window.location.search);
  const themeId = params.get(PREVIEW_THEME_PARAM);
  const flagged = params.get(QA_PARAM) === '1';

  if (!flagged && !themeId && getSessionTag('qa_session') !== 'yes') return;

  setSessionTag('qa_session', 'yes');
  if (themeId) setSessionTag('qa_theme_id', themeId);

  // A QA session exists to be watched — it must never be dropped by sampling.
  upgradeSession('qa_session');
};

/**
 * Links every session of a logged-in shopper under one Clarity user, so a
 * search session and a later order can be joined against Shopify order data.
 * The id is already rendered into the page by theme.liquid for the wishlist app.
 */
const initIdentity = () => {
  const customerId = document.querySelector(SELECTORS.customerIdNode)?.dataset.customerId ?? '';
  if (!customerId) {
    setSessionTag('logged_in', 'no');
    return;
  }

  callClarity('identify', customerId);
  setSessionTag('logged_in', 'yes');
  setSessionTag('customer_id', customerId);
};

/**
 * Shopify's checkout runs on its own pages, so no theme code — and therefore no
 * clarity() call — executes there. What the theme can do is record that the
 * shopper left for checkout, then look at the cart when they come back.
 * localStorage (not sessionStorage) because the checkout excursion can span
 * tabs, and the marker has to outlive the storefront tab being closed.
 */
const markCheckoutHandoff = () => {
  try {
    localStorage.setItem(
      CHECKOUT_HANDOFF_KEY,
      JSON.stringify({ at: Date.now(), usedSearch: usedSearch(), term: getSessionTag('search_term') ?? '' })
    );
  } catch (error) {
    /* Storage unavailable — the conversion proxy is simply skipped. */
  }
};

const clearCheckoutHandoff = () => {
  try {
    localStorage.removeItem(CHECKOUT_HANDOFF_KEY);
  } catch (error) {
    /* Nothing to clean up. */
  }
};

/**
 * A shopper who clicked checkout and is now back on the storefront with an
 * empty cart almost certainly completed the order — Shopify clears the cart on
 * a successful checkout, and the thank-you page links straight back here.
 *
 * This is a PROXY, not the order webhook: a manually emptied cart looks the
 * same. It is tagged `converted_proxy` rather than `converted` so nobody reads
 * it as a confirmed purchase. See docs/clarity-tracking-audit.md.
 */
const resolveCheckoutHandoff = () => {
  let marker = null;
  try {
    marker = JSON.parse(localStorage.getItem(CHECKOUT_HANDOFF_KEY) ?? 'null');
  } catch (error) {
    marker = null;
  }
  if (!marker?.at) return;

  if (Date.now() - marker.at > CHECKOUT_HANDOFF_TTL_MS) {
    clearCheckoutHandoff();
    return;
  }

  const count = renderedCartCount();
  if (count === null || count > 0) return;

  sendEvent('purchase_likely_completed');
  setSessionTag('converted_proxy', 'yes');
  if (marker.usedSearch) {
    sendEvent('purchase_likely_completed_after_search');
    setSessionTag('converted_proxy_after_search', 'yes');
    if (marker.term) setSessionTag('converting_search_term', marker.term);
  }
  upgradeSession('conversion_proxy');
  clearCheckoutHandoff();
};

/* -------------------------------------------------------------------------- */
/* Product detail page                                                         */
/* -------------------------------------------------------------------------- */

/** PDP handlers are bound sitewide so the quick-view modal reuses them. */
const pdpContext = (element) =>
  element.closest(SELECTORS.quickViewOverlay) ? 'quick_view' : 'product_page';

const trackProductPageView = () => {
  if (!isTemplate('product')) return;

  const name = document.querySelector(SELECTORS.pdpTitle)?.textContent?.trim() ?? '';

  sendEvent('pdp_viewed');
  if (name) setTag('pdp_product', name);
  if (usedSearch()) sendEvent('pdp_viewed_after_search');

  if (document.querySelector(SELECTORS.pdpSoldOut)) {
    sendEvent('pdp_sold_out_viewed');
    setTag('pdp_sold_out', 'yes');
    upgradeSession('pdp_sold_out');
  }
};

const initPdpTracking = () => {
  document.addEventListener(
    'click',
    (event) => {
      const target = elementTarget(event);
      if (!target) return;

      if (target.closest(SELECTORS.pdpVariantOption)) {
        const option = target.closest(SELECTORS.pdpVariantOption);
        sendEvent('pdp_variant_selected');
        setTag('pdp_variant', labelFrom(option));
        setTag('pdp_variant_context', pdpContext(option));
        return;
      }

      if (target.closest(SELECTORS.pdpVariantDropdown)) {
        sendEvent('pdp_variant_dropdown_opened');
        return;
      }

      const accordionButton = target.closest(SELECTORS.pdpAccordionButton);
      if (accordionButton) {
        // aria-expanded is flipped by pdp-variants.js after this handler runs,
        // so the value read here is the state being left behind.
        const wasOpen = accordionButton.getAttribute('aria-expanded') === 'true';
        sendEvent(wasOpen ? 'pdp_accordion_closed' : 'pdp_accordion_opened');
        setTag('pdp_accordion', labelFrom(accordionButton.querySelector(SELECTORS.pdpAccordionTitle) ?? accordionButton));
        return;
      }

      if (target.closest(SELECTORS.pdpDescriptionExpand)) {
        sendEvent('pdp_description_expanded');
        return;
      }

      if (target.closest(SELECTORS.pdpGalleryArrow)) {
        sendEvent('pdp_gallery_navigated');
        return;
      }

      if (target.closest(SELECTORS.pdpQuantityIncrement)) {
        sendEvent('pdp_quantity_increased');
        return;
      }

      if (target.closest(SELECTORS.pdpQuantityDecrement)) {
        sendEvent('pdp_quantity_decreased');
        return;
      }

      if (target.closest(SELECTORS.pdpWriteReview)) {
        sendEvent('pdp_write_review_clicked');
        return;
      }

      if (target.closest(SELECTORS.pdpReviews)) {
        sendEvent('pdp_reviews_clicked');
        return;
      }

      if (target.closest(SELECTORS.pdpPaymentInfo)) sendEvent('pdp_payment_info_clicked');
    },
    true
  );

  trackProductPageView();
};

/* -------------------------------------------------------------------------- */
/* Quick view                                                                  */
/* -------------------------------------------------------------------------- */

const initQuickViewTracking = () => {
  document.addEventListener(
    'click',
    (event) => {
      const target = elementTarget(event);
      if (!target) return;

      const trigger = target.closest(SELECTORS.quickViewTrigger);
      if (trigger) {
        sendEvent('quick_view_opened');
        if (trigger.dataset.handle) setTag('quick_view_product', trigger.dataset.handle);
        setTag('quick_view_source', productCardSource(trigger));
        upgradeSession('quick_view');
        return;
      }

      const overlay = document.querySelector(SELECTORS.quickViewOverlay);
      if (!overlay || overlay.classList.contains('hidden')) return;

      // quick-view.js closes on the close button and on a click landing on the
      // overlay backdrop itself — mirror both conditions rather than guessing.
      if (target.closest(SELECTORS.quickViewClose) || target === overlay) {
        sendEvent('quick_view_closed');
      }
    },
    true
  );
};

/* -------------------------------------------------------------------------- */
/* Collection / PLP                                                            */
/* -------------------------------------------------------------------------- */

const trackCollectionPageView = () => {
  if (!isTemplate('collection') && !isTemplate('list-collections')) return;

  sendEvent('collection_viewed');
  setTag('collection_handle', window.location.pathname);

  // A collection that renders no tiles is almost always an over-filtered URL.
  if (document.querySelector(SELECTORS.plpEmpty)) {
    sendEvent('plp_zero_results');
    setSessionTag('plp_zero_results', 'yes');
    setTag('plp_zero_results_query', window.location.search.slice(0, TERM_MAX_LENGTH));
    upgradeSession('plp_zero_results');
  }
};

/**
 * Filter and sort do not reload the page: main.js fetches the filtered URL,
 * swaps `[collection-items-wrapper]`.innerHTML and pushState's the new URL. The
 * load-time check in trackCollectionPageView() therefore only ever sees the
 * unfiltered grid, so the result of a filter has to be observed.
 */
const observeCollectionResults = () => {
  const wrapper = document.querySelector(SELECTORS.plpItemsWrapper);
  if (!wrapper) return;

  let lastState = '';

  const observer = new MutationObserver(() => {
    const isEmpty = Boolean(wrapper.querySelector(SELECTORS.plpEmpty));
    const count = wrapper.querySelector(SELECTORS.plpItemsCount)?.dataset.count ?? '';

    // innerHTML swaps fire several mutations; key off what the shopper can see.
    const state = `${isEmpty}|${count}|${window.location.search}`;
    if (state === lastState) return;
    lastState = state;

    sendEvent('plp_results_updated');
    if (count) setTag('plp_results_count', count);

    if (!isEmpty) return;

    sendEvent('plp_zero_results');
    setSessionTag('plp_zero_results', 'yes');
    setTag('plp_zero_results_query', window.location.search.slice(0, TERM_MAX_LENGTH));
    upgradeSession('plp_zero_results');
  });

  observer.observe(wrapper, { childList: true, subtree: true });
};

const initCollectionTracking = () => {
  document.addEventListener(
    'click',
    (event) => {
      const target = elementTarget(event);
      if (!target) return;

      const filterOption = target.closest(SELECTORS.plpFilterOption);
      if (filterOption) {
        // The --active class is removed after this handler, so its presence
        // means the click is clearing the filter rather than applying it.
        const wasActive = filterOption.classList.contains(
          'collection-listing__sortings-filtering-option-btn--active'
        );
        sendEvent(wasActive ? 'plp_filter_removed' : 'plp_filter_applied');
        setTag('plp_filter_group', filterOption.dataset.filter ?? '');
        setTag('plp_filter_value', filterOption.dataset.value ?? labelFrom(filterOption));
        return;
      }

      const sortOption = target.closest(SELECTORS.plpSortOption);
      if (sortOption) {
        sendEvent('plp_sort_applied');
        setTag('plp_sort_value', sortOption.dataset.value ?? labelFrom(sortOption));
        return;
      }

      const sortingsToggle = target.closest(SELECTORS.plpSortingsToggle);
      if (sortingsToggle) {
        const panel = sortingsToggle.getAttribute('aria-controls');
        sendEvent(panel === 'sorting' ? 'plp_sort_panel_opened' : 'plp_filter_panel_opened');
        return;
      }

      if (target.closest(SELECTORS.plpPromoBanner)) {
        sendEvent('plp_promo_banner_clicked');
        return;
      }

      const subcollection = target.closest(SELECTORS.plpSubcollection);
      if (subcollection) {
        sendEvent('plp_subcollection_clicked');
        setTag('plp_subcollection', labelFrom(subcollection));
        return;
      }

      const pageLink = target.closest(SELECTORS.pagination);
      if (pageLink) {
        sendEvent('plp_pagination_clicked');
        setTag('plp_page', labelFrom(pageLink));
        return;
      }

      const cardLink = target.closest(SELECTORS.productCardLink);
      if (!cardLink) return;

      const card = cardLink.closest(SELECTORS.productCard);
      sendEvent('product_card_clicked');
      setTag('product_card_source', card ? productCardSource(card) : 'other');
      const name = productNameFrom(card);
      if (name) setTag('product_card_product', name);
    },
    true
  );

  observeCollectionResults();
  trackCollectionPageView();
};

/* -------------------------------------------------------------------------- */
/* Navigation                                                                  */
/* -------------------------------------------------------------------------- */

/** A nav item that reveals a deeper tier rather than navigating anywhere. */
const HAS_SUB_CLASSES = [
  'header__menu-item-link--has-sub',
  'header__mob-menu-item-link--has-sub',
  'header__mob-menu-item-sub-link--has-sub',
];

const opensSubMenu = (element) => HAS_SUB_CLASSES.some((name) => element.classList.contains(name));

const initNavigationTracking = () => {
  document.addEventListener(
    'click',
    (event) => {
      const target = elementTarget(event);
      if (!target) return;

      if (target.closest(SELECTORS.navMobileTrigger)) {
        sendEvent('nav_mobile_menu_opened');
        return;
      }

      if (target.closest(SELECTORS.navMobileClose)) {
        sendEvent('nav_mobile_menu_closed');
        return;
      }

      const subLink = target.closest(SELECTORS.navSubLink);
      if (subLink) {
        sendEvent(opensSubMenu(subLink) ? 'nav_sub_level_opened' : 'nav_sub_link_clicked');
        setTag('nav_sub_link', labelFrom(subLink));
        setTag('nav_device', deviceLabel());
        return;
      }

      const menuLink = target.closest(SELECTORS.navMenuLink);
      if (menuLink) {
        sendEvent(opensSubMenu(menuLink) ? 'nav_top_level_opened' : 'nav_top_level_clicked');
        setTag('nav_top_level', labelFrom(menuLink));
        setTag('nav_device', deviceLabel());
        return;
      }

      if (target.closest(SELECTORS.navAccount)) {
        sendEvent('nav_account_clicked');
        return;
      }

      if (target.closest(SELECTORS.navLogo)) {
        sendEvent('nav_logo_clicked');
        return;
      }

      const announcement = target.closest(SELECTORS.announcementLink);
      if (announcement) {
        sendEvent('announcement_bar_clicked');
        setTag('announcement_label', labelFrom(announcement));
        return;
      }

      const footerLink = target.closest(SELECTORS.footerLink);
      if (footerLink) {
        sendEvent('footer_link_clicked');
        setTag('footer_link', labelFrom(footerLink));
      }
    },
    true
  );
};

/* -------------------------------------------------------------------------- */
/* Wishlist                                                                    */
/* -------------------------------------------------------------------------- */

const initWishlistTracking = () => {
  document.addEventListener(
    'click',
    (event) => {
      const target = elementTarget(event);
      if (!target) return;

      const toggle = target.closest(SELECTORS.wishlistToggle);
      if (toggle) {
        sendEvent('wishlist_toggled');
        const name = productNameFrom(toggle.closest(SELECTORS.productCard));
        if (name) setTag('wishlist_product', name);
        return;
      }

      if (target.closest(SELECTORS.wishlistView)) sendEvent('wishlist_page_opened');
    },
    true
  );
};

/* -------------------------------------------------------------------------- */
/* Forms — newsletter, contact, wholesale, account                             */
/* -------------------------------------------------------------------------- */

/**
 * Every Shopify {% form %} renders a hidden `form_type` input, so one delegated
 * submit listener covers newsletter, contact and all four account forms without
 * needing a selector per form.
 */
const FORM_EVENTS = {
  customer: 'newsletter_submitted',
  contact: 'contact_form_submitted',
  customer_login: 'account_login_submitted',
  create_customer: 'account_register_submitted',
  recover_customer_password: 'account_password_recovery_submitted',
  reset_customer_password: 'account_password_reset_submitted',
};

const initFormTracking = () => {
  document.addEventListener(
    'submit',
    (event) => {
      const form = elementTarget(event)?.closest('form');
      if (!form) return;

      const formType = form.querySelector('[name="form_type"]')?.value ?? '';
      const eventName = FORM_EVENTS[formType];
      if (!eventName) return;

      sendEvent(eventName);
      setTag('form_submitted', formType);
      upgradeSession('form_submission');
    },
    true
  );
};

/**
 * Newsletter outcome.
 *
 * The footer form is a plain Shopify {% form 'customer' %} POST, so the success
 * node is normally server-rendered and already present when this runs. A single
 * load-time check is still not enough: it is the last thing init() does, and any
 * section swap or app script that re-renders the footer after load lands the node
 * too late to be seen. So check once, then keep watching until an outcome shows
 * up, and report whichever arrives first exactly once.
 */
const trackNewsletterOutcome = () => {
  let reported = false;

  const report = () => {
    if (reported) return true;

    if (document.querySelector(SELECTORS.newsletterSuccess)) {
      reported = true;
      sendEvent('newsletter_signup_succeeded');
      setSessionTag('newsletter_subscribed', 'yes');
      return true;
    }

    if (document.querySelector(SELECTORS.newsletterError)?.textContent?.trim()) {
      reported = true;
      sendEvent('newsletter_signup_failed');
      upgradeSession('form_error');
      return true;
    }

    return false;
  };

  if (report()) return;

  const observer = new MutationObserver(() => {
    if (report()) observer.disconnect();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // A body-wide observer must not outlive the window in which a submit could
  // plausibly resolve.
  window.setTimeout(() => observer.disconnect(), NEWSLETTER_OUTCOME_WATCH_MS);
};

/**
 * Account form outcomes. Shopify re-renders the page after an account form post,
 * so these are read from the server-rendered markup on load.
 */
const trackFormOutcomes = () => {
  const accountError = document.querySelector(SELECTORS.accountErrorSummary);
  if (accountError?.textContent?.trim()) {
    sendEvent('account_form_failed');
    setTag('account_form_error', labelFrom(accountError));
    setTag('account_form_template', templateName());
    upgradeSession('account_form_error');
    return;
  }

  // Reset/activate templates mark the offending field instead of rendering a
  // summary block, so fall back to the input state.
  if (document.querySelector(SELECTORS.accountFieldError)) {
    sendEvent('account_form_failed');
    setTag('account_form_template', templateName());
    upgradeSession('account_form_error');
  }
};

/* -------------------------------------------------------------------------- */
/* Content — hero, article, 404, outbound social                               */
/* -------------------------------------------------------------------------- */

const trackArticleDepth = () => {
  const content = document.querySelector(SELECTORS.articleContent);
  if (!content) return;

  const reached = new Set();

  const onScroll = () => {
    const { top, height } = content.getBoundingClientRect();
    if (height <= 0) return;

    const read = Math.min(100, Math.max(0, ((window.innerHeight - top) / height) * 100));

    for (const mark of ARTICLE_DEPTH_MARKS) {
      if (read < mark || reached.has(mark)) continue;
      reached.add(mark);
      sendEvent(`article_read_${mark}`);
    }

    if (reached.size === ARTICLE_DEPTH_MARKS.length) {
      window.removeEventListener('scroll', onScroll);
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
};

const trackContentPageView = () => {
  if (isTemplate('404')) {
    sendEvent('page_not_found');
    setTag('not_found_path', window.location.pathname.slice(0, TERM_MAX_LENGTH));
    setTag('not_found_referrer', document.referrer.slice(0, TERM_MAX_LENGTH));
    upgradeSession('page_not_found');
    return;
  }

  if (isTemplate('article')) {
    sendEvent('article_viewed');
    setTag('article_path', window.location.pathname.slice(0, TERM_MAX_LENGTH));
    trackArticleDepth();
    return;
  }

  // Viewing a past order is the only order confirmation the theme ever renders.
  if (isTemplate('customers/order')) sendEvent('account_order_viewed');
};

const initContentTracking = () => {
  document.addEventListener(
    'click',
    (event) => {
      const target = elementTarget(event);
      if (!target) return;

      const cta = target.closest(SELECTORS.heroCta);
      if (cta) {
        sendEvent('hero_cta_clicked');
        setTag('hero_cta', labelFrom(cta));
        return;
      }

      const social = target.closest(SELECTORS.socialLink);
      if (social) {
        sendEvent('social_link_clicked');
        setTag('social_destination', new URL(social.href, window.location.origin).hostname);
      }
    },
    true
  );

  trackContentPageView();
};

/* -------------------------------------------------------------------------- */
/* Frustration signals                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Clarity detects rage and dead clicks natively, but not thrown JS. Capping the
 * count keeps a loop in third-party code from flooding the event stream.
 */
const initErrorTracking = () => {
  let reported = 0;

  const report = (message) => {
    if (reported >= MAX_JS_ERRORS_PER_PAGE) return;
    reported += 1;

    sendEvent('js_error');
    setTag('js_error_message', String(message ?? 'unknown').slice(0, TERM_MAX_LENGTH));
    setTag('js_error_template', templateName());
    upgradeSession('js_error');
  };

  window.addEventListener('error', (event) => report(event.message));
  window.addEventListener('unhandledrejection', (event) => report(event.reason?.message ?? event.reason));
};

/* -------------------------------------------------------------------------- */
/* Boot                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Run one boot step in isolation. Without this a single throwing module takes
 * every later step down with it, which reads as "some events fire, some never
 * do" — and the steps that report page state rather than clicks are last, so
 * they are the ones that silently disappear.
 */
const runStep = (name, step) => {
  try {
    step();
  } catch (error) {
    if (isDebug) console.log('%c[clarity]', 'color:#7c3aed;font-weight:700', 'step failed:', name, error);
  }
};

const init = () => {
  runStep('replaySessionTags', replaySessionTags);
  runStep('pageTemplate', () => setTag('page_template', templateName()));
  runStep('initQaSession', initQaSession);
  runStep('initIdentity', initIdentity);

  runStep('initSearchTracking', initSearchTracking);
  runStep('initCartTracking', initCartTracking);
  runStep('initPdpTracking', initPdpTracking);
  runStep('initQuickViewTracking', initQuickViewTracking);
  runStep('initCollectionTracking', initCollectionTracking);
  runStep('initNavigationTracking', initNavigationTracking);
  runStep('initWishlistTracking', initWishlistTracking);
  runStep('initFormTracking', initFormTracking);
  runStep('initContentTracking', initContentTracking);
  runStep('initErrorTracking', initErrorTracking);

  runStep('trackSearchResultsPage', trackSearchResultsPage);
  runStep('trackNewsletterOutcome', trackNewsletterOutcome);
  runStep('trackFormOutcomes', trackFormOutcomes);
  runStep('resolveCheckoutHandoff', resolveCheckoutHandoff);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
